import { Button, Input, Popconfirm, Space, Table, Tag, Tooltip, Typography } from 'antd'
import { PlusOutlined } from '@ant-design/icons'
import { useMemo, useState } from 'react'
import dayjs from 'dayjs'
import { useDebouncedState } from '../../../hooks/useDebouncedState'
import VSpace from '../../common/VSpace'
import { FeatureFlag } from '../../../domain/features/featureFlags'
import { useDeleteFeatureFlagMutation, useFeatureFlagsQuery } from './featureFlagsQueries'
import {
  filterFeatureFlags,
  formatLastUsedAt,
  formatLastUsedRelative,
  getTreatmentTagColor,
  hasRecentTraffic,
  RECENT_TRAFFIC_DAYS,
} from './featureFlagsModel'
import FeatureFlagEditorModal from './FeatureFlagEditorModal'

type EditorState = {
  open: boolean
  editing: FeatureFlag | null
}

const FeatureFlagsPanel = () => {
  const { data, isLoading } = useFeatureFlagsQuery()
  const deleteMutation = useDeleteFeatureFlagMutation()
  const [editorState, setEditorState] = useState<EditorState>({ open: false, editing: null })
  const { state: search, debouncedState, setState: setSearch } = useDebouncedState('', 200)

  const features = data?.features ?? []

  const filteredFeatures = useMemo(
    () => filterFeatureFlags(features, debouncedState),
    [features, debouncedState],
  )

  const columns = [
    {
      title: 'Id',
      dataIndex: 'id',
      sorter: (a: FeatureFlag, b: FeatureFlag) => a.id.localeCompare(b.id),
      render: (id: string, flag: FeatureFlag) => (
        <Typography.Link onClick={() => setEditorState({ open: true, editing: flag })}>
          {id}
        </Typography.Link>
      ),
    },
    {
      title: 'Treatments',
      dataIndex: 'treatments',
      render: (treatments: string[]) => treatments.map((treatment) => (
        <Tag key={treatment}>{treatment}</Tag>
      )),
    },
    {
      title: 'Default Treatment',
      dataIndex: 'defaultTreatment',
      render: (defaultTreatment: string) => (
        <Tag color={getTreatmentTagColor(defaultTreatment)}>{defaultTreatment}</Tag>
      ),
    },
    {
      title: 'Rules',
      dataIndex: 'rules',
      render: (rules: FeatureFlag['rules']) => rules.length,
    },
    {
      title: 'Tags',
      dataIndex: 'tags',
      render: (tags: FeatureFlag['tags']) => (
        <Space size={[4, 4]} wrap>
          {tags.map((tag) => (
            <Tag key={tag.label} color={tag.color ?? undefined}>
              {tag.label}
              <Typography.Text type="secondary" style={{ fontSize: 11, marginLeft: 6 }}>
                {dayjs(tag.timestamp).format('MMM D, YYYY h:mm A')}
              </Typography.Text>
            </Tag>
          ))}
        </Space>
      ),
    },
    {
      title: 'Last Usage',
      key: 'lastUsage',
      render: (_: unknown, flag: FeatureFlag) => {
        if (!flag.lastUsedAt) {
          return <Typography.Text type="secondary">Never</Typography.Text>
        }
        return (
          <>
            <Tooltip title={formatLastUsedAt(flag.lastUsedAt)}>
              <span>{formatLastUsedRelative(flag.lastUsedAt)}</span>
            </Tooltip>
            {flag.lastClientVersion && (
              <Typography.Text type="secondary" style={{ fontSize: 11, marginLeft: 6 }}>
                {flag.lastClientVersion}
              </Typography.Text>
            )}
          </>
        )
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: unknown, flag: FeatureFlag) => (
        <>
          <Button
            type="link"
            size="small"
            onClick={() => setEditorState({ open: true, editing: flag })}
          >
            Edit
          </Button>
          <Popconfirm
            title={`Permanently delete '${flag.id}' and all of its rules?`}
            description={
              hasRecentTraffic(flag.lastUsedAt) ? (
                <Typography.Text type="warning" style={{ display: 'block', maxWidth: 260 }}>
                  This flag has received traffic in the last {RECENT_TRAFFIC_DAYS} days (last used{' '}
                  {formatLastUsedRelative(flag.lastUsedAt)}). Deleting it may affect active users.
                </Typography.Text>
              ) : undefined
            }
            okText="Delete"
            okButtonProps={{ danger: true }}
            onConfirm={() => deleteMutation.mutate(flag.id)}
          >
            <Button type="link" size="small" danger>
              Delete
            </Button>
          </Popconfirm>
        </>
      ),
    },
  ]

  return (
    <VSpace>
      <Space>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => setEditorState({ open: true, editing: null })}
        >
          New Feature Flag
        </Button>
        <Input.Search
          placeholder="Search by id or tag"
          allowClear
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </Space>
      <Table<FeatureFlag>
        dataSource={filteredFeatures}
        loading={isLoading}
        rowKey="id"
        columns={columns}
        pagination={false}
      />
      <FeatureFlagEditorModal
        open={editorState.open}
        editing={editorState.editing}
        existingFlagIds={features.map((flag) => flag.id)}
        onClose={() => setEditorState({ open: false, editing: null })}
      />
    </VSpace>
  )
}

export default FeatureFlagsPanel
